<template>
  <div class>
    <Toast />

    <div class="card css-table">
      <div class="p-mb-4">
        <h2 class="p-m-0">Cetak Ulang Barcode</h2>
        <p class="p-text-secondary p-mt-2">
          Kelola dan cetak ulang barcode untuk item yang sudah ada.
        </p>
      </div>

      <DataTable
        :value="list_barcodes"
        :loading="loading.filter"
        :rowHover="true"
        :selection.sync="selectedItems"
        :paginator="true"
        :rows="pagination.rows"
        :rowsPerPageOptions="[10, 20, 50]"
        :totalRecords="pagination.totalRecords"
        :lazy="true"
        :first="pagination.first"
        @page="onPage($event)"
        :filters="filters"
        filterDisplay="menu"
        :globalFilterFields="[
          'supplier_code',
          'name',
          'total_item',
          'barcode_date',
          'total_qty',
          'price'
        ]"
        :scrollable="true"
        scrollHeight="400px"
        class="p-datatable-sm"
        ref="dt"
      >
        <template #empty>
          <div class="p-d-flex p-jc-center p-ai-center p-p-4">
            <i class="pi pi-info-circle p-mr-2"></i>
            <span>Data tidak tersedia.</span>
          </div>
        </template>
        <template #loading>
          <div class="p-d-flex p-jc-center p-ai-center p-p-4">
            <i class="pi pi-spin pi-spinner p-mr-2"></i>
            <span>Mengambil data daftar cetak barang. Mohon tunggu.</span>
          </div>
        </template>
        <template #header>
          <div class="p-grid p-fluid p-mb-3">
            <div class="p-col-12 p-md-6 p-lg-3">
              <label for="supplier">Supplier</label>
              <AutoComplete
                id="supplier"
                placeholder="Pilih Supplier"
                :dropdown="true"
                :multiple="false"
                v-model="selected_supplier"
                :suggestions="suppliers"
                @complete="searchSupplier($event)"
                field="code"
                :class="{ 'p-invalid': is_error.supplier }"
              >
                <template #item="slotProps">
                  <div>{{ slotProps.item.name }}</div>
                  <div style="font-style: italic; font-size: 12px">
                    {{ slotProps.item.code }}
                  </div>
                </template>
              </AutoComplete>
              <small class="p-error" v-if="is_error.supplier">{{
                error_msg.supplier
              }}</small>
            </div>
            <div class="p-col-12 p-md-6 p-lg-3">
              <label for="outlet">Pilih Outlet</label>
              <AutoComplete
                ref="outletDropdown"
                id="outlet"
                placeholder="Toko"
                :dropdown="true"
                :multiple="false"
                v-model="selectedOutlet"
                :suggestions="autoFilteredValue"
                field="name"
                @complete="searchOutlets($event)"
                :class="{ 'p-invalid': is_error.outlet }"
              />
              <small class="p-error" v-if="is_error.outlet">{{
                error_msg.outlet
              }}</small>
            </div>
            <div class="p-col-12 p-md-6 p-lg-3">
              <label for="barcode_date">Tanggal Barcode</label>
              <Calendar
                id="barcode_date"
                v-model="filter.barcode_date"
                :showIcon="true"
                dateFormat="dd/mm/yy"
                :maxDate="today"
                :class="{ 'p-invalid': is_error.date }"
              />
              <small class="p-error" v-if="is_error.date">{{
                error_msg.date
              }}</small>
            </div>
            <div class="p-col-12 p-md-6 p-lg-3" style="padding-top: 33px">
              <Button
                label="Cari Barcode"
                icon="pi pi-search"
                class="p-button-primary w-full"
                :loading="loading.filter"
                @click="getList()"
              />
            </div>
          </div>
        </template>

        <Column
          field="cat_no"
          header="No"
          headerStyle="width: 4em; text-align: center"
          bodyStyle="width: 4em; text-align: center"
        >
          <template #body="slotProps">
            {{ list_barcodes.indexOf(slotProps.data) + 1 }}
          </template>
        </Column>
        <Column
          field="supplier_code"
          header="Kode Supplier"
          headerStyle="width: 10em; text-align: center"
          bodyStyle="width: 10em; text-align: center"
          :filter="true"
        >
          <template #body="slotProps">
            <span class="truncate-cell" :title="slotProps.data.supplier_code">
              {{ slotProps.data.supplier_code }}
            </span>
          </template>
        </Column>
        <Column
          field="name"
          header="Nama Supplier"
          headerStyle="width: 15em; text-align: center"
          bodyStyle="width: 15em"
          :filter="true"
        >
          <template #body="slotProps">
            <span class="truncate-cell" :title="slotProps.data.name">
              {{ slotProps.data.name }}
            </span>
          </template>
        </Column>
        <Column
          field="total_item"
          header="Jenis"
          headerStyle="width: 6em; text-align: center"
          bodyStyle="width: 6em; text-align: center"
          :filter="true"
        >
          <template #body="slotProps">
            <span>{{ slotProps.data.total_item }}</span>
          </template>
        </Column>
        <Column
          field="barcode_date"
          header="Tanggal Barcode"
          headerStyle="width: 10em; text-align: center"
          bodyStyle="width: 10em; text-align: center"
          :filter="true"
        >
          <template #body="slotProps">
            <span>{{ formatDate(slotProps.data.barcode_date) }}</span>
          </template>
        </Column>
        <Column
          field="total_qty"
          header="Jumlah"
          headerStyle="width: 6em; text-align: center"
          bodyStyle="width: 6em; text-align: right"
          :filter="true"
        >
          <template #body="slotProps">
            <span>{{ slotProps.data.total_qty }}</span>
          </template>
        </Column>
        <Column
          field="price"
          header="Harga"
          headerStyle="width: 8em; text-align: center"
          bodyStyle="width: 8em; text-align: right"
          :filter="true"
        >
          <template #body="slotProps">
            <span>{{ slotProps.data.price | currency }}</span>
          </template>
        </Column>
        <Column
          selectionMode="multiple"
          headerStyle="width: 4em; text-align: center"
          bodyStyle="width: 4em; text-align: center"
        />
      </DataTable>

      <div
        class="p-grid p-justify-center p-mt-4"
        v-if="list_barcodes.length != 0 && selectedItems.length != 0"
      >
        <div class="p-col-12 p-md-6 p-lg-3">
          <Button
            label="Cetak"
            icon="pi pi-print"
            class="p-button-primary w-full"
            @click="printBarcode()"
            :loading="loading.print"
          />
        </div>
      </div>

      <Dialog
        :visible.sync="print_dialog"
        :style="{ width: '90vw', maxWidth: '1000px' }"
        header="Konfirmasi Cetak Barcode"
        :modal="true"
        class="p-fluid"
      >
        <div class="confirmation-content">
          <div class="p-mb-3">
            <i class="pi pi-print p-mr-3" style="font-size: 1rem" />
            <span
              >Apakah Anda yakin ingin mencetak barcode untuk item yang
              dipilih?</span
            >
          </div>

          <DataTable
            :value="selectedItems"
            :scrollable="true"
            scrollHeight="400px"
            class="p-mt-3 confirmation-table"
          >
            <Column
              field="supplier_code"
              header="Kode Supplier"
              headerStyle="width: 10em; text-align: center"
              bodyStyle="width: 10em"
            >
              <template #body="slotProps">
                <span
                  class="truncate-cell"
                  :title="slotProps.data.supplier_code"
                >
                  {{ slotProps.data.supplier_code }}
                </span>
              </template>
            </Column>
            <Column
              field="name"
              header="Nama Supplier"
              headerStyle="width: 15em; text-align: center"
              bodyStyle="width: 15em"
            >
              <template #body="slotProps">
                <span class="truncate-cell" :title="slotProps.data.name">
                  {{ slotProps.data.name }}
                </span>
              </template>
            </Column>
            <Column
              field="total_item"
              header="Jenis"
              headerStyle="width: 6em; text-align: center"
              bodyStyle="width: 6em; text-align: right"
            >
              <template #body="slotProps">
                {{ slotProps.data.total_item }}
              </template>
            </Column>
            <Column
              field="total_qty"
              header="Jumlah"
              headerStyle="width: 6em; text-align: center"
              bodyStyle="width: 6em; text-align: right"
            >
              <template #body="slotProps">
                {{ slotProps.data.total_qty }}
              </template>
            </Column>
            <Column
              field="barcode_date"
              header="Tanggal"
              headerStyle="width: 10em; text-align: center"
              bodyStyle="width: 10em; text-align: center"
            >
              <template #body="slotProps">
                {{ formatDate(slotProps.data.barcode_date) }}
              </template>
            </Column>
          </DataTable>
        </div>
        <template #footer>
          <Button
            label="Batal"
            icon="pi pi-times"
            class="p-button-text"
            @click="print_dialog = false"
          />
          <Button
            label="Cetak"
            icon="pi pi-check"
            class="p-button-primary"
            @click="saveClicked()"
            :loading="loading_ok"
          />
        </template>
      </Dialog>
    </div>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
let moment = require('moment')
export default {
  data() {
    return {
      moment: moment,
      outlets: [],
      selected_outlet: '',
      today: new Date(),
      filters: {},

      loading: {
        filter: false
      },

      filter: {
        show_all: false,
        doc_number: '',
        doc_date: '',
        sup_id: '',
        selected_supplier: '',
        barcode_date: '',
        supplier_value: true
      },

      autoFilteredValue: [],
      selectedOutlets: null,
      selectedOutlet: { id: '', code: '', name: '' },
      print_dialog: false,

      request_api: [],
      list_request: {
        sup_id: 0,
        barcode_date: ''
      },

      suppliers: [],
      filtered_suppliers: [],
      selected_supplier: '',

      limit: 1000,
      loading_ok: false,

      list_barcodes: [],

      warehouse_outbounds: [],
      selectedItems: [],
      selectedItems_id: '',
      ids: [],

      barcodes: null,

      is_error: {
        supplier: false,
        outlet: false
      },

      error_msg: {
        supplier: '',
        outlet: ''
      },

      // Pagination state
      pagination: {
        first: 0,
        rows: 10,
        offset: 0,
        totalRecords: 0
      }
    }
  },
  mounted() {
    this.getList()
    this.getListOutlets()
    this.searchSupplier()
  },
  methods: {
    showAll() {
      this.warehouse_outbounds = []
      this.selected_supplier = ''
      this.selected_outlet = ''
      this.list_barcodes = []
    },

    chooseOutlets() {},
    searchOutlets(event) {
      this.autoFilteredValue = this.selectedOutlets.filter(item => {
        return (
          item.code.toLowerCase().includes(event.query.toLowerCase()) ||
          item.name.toLowerCase().includes(event.query.toLowerCase())
        )
      })
    },

    async getListOutlets() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/outlets`
        )

        if (response.status === 200) {
          this.selectedOutlets = response.data.data.outlets
        } else {
          console.error('Gagal mendapatkan data outlet')
        }
      } catch (error) {
        console.error('Terjadi kesalahan saat mengambil data outlet:', error)
      }
    },

    searchSupplier(event) {
      this.$http
        .get(
          `${process.env.VUE_APP_API_URL}/suppliers?search=${
            this.selected_supplier instanceof Object
              ? this.selected_supplier.name
              : this.selected_supplier
              ? this.selected_supplier
              : ''
          }&limit=5`
        )
        .then(response => {
          if (response.status === 200) {
            this.suppliers = response.data.data.suppliers
          }
        })
      this.filtered_suppliers = this.suppliers.filter(item => {
        return (
          item.name.toLowerCase().startsWith(event.query.toLowerCase()) ||
          item.code.toLowerCase().startsWith(event.query.toLowerCase())
        )
      })
    },

    async getList() {
      this.loading.filter = true

      try {
        let out_id = (this.selectedOutlet && this.selectedOutlet.id) || ''
        let sup_code =
          (this.selected_supplier && this.selected_supplier.code) || ''
        // Calendar yields a Date; API requires YYYY-MM-DD string.
        let barcode_date = this.filter.barcode_date
          ? moment(this.filter.barcode_date).format('YYYY-MM-DD')
          : ''

        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/dashboard/warehouse-outbounds/barcode-repeat/list?status_generate_barcode=1&barcode_date=${barcode_date}&outlet_id=${out_id}&sup_code=${sup_code}&limit=${this.pagination.rows}&offset=${this.pagination.offset}`
        )

        if (response.status === 200) {
          this.list_barcodes = response.data.data.barcode || []
          this.pagination.totalRecords = response.data.data.total || 0

          if (this.list_barcodes.length === 0) {
            this.showToast('info', 'Info', 'Tidak ada data yang ditemukan')
          }
        } else {
          this.showToast('error', 'Error', 'Gagal mengambil data barcode')
        }
      } catch (error) {
        console.error('Error fetching barcode list:', error)
        this.showToast(
          'error',
          'Error',
          'Terjadi kesalahan saat mengambil data barcode'
        )
      } finally {
        this.loading.filter = false
      }
    },

    onPage(event) {
      this.pagination.first = event.first
      this.pagination.rows = event.rows
      this.pagination.offset = event.first
      this.getList()
    },

    saveClicked() {
      this.loading_ok = true
      let itemsArray = []
      for (let i = 0; i < this.selectedItems.length; i++) {
        let item = this.selectedItems[i]
        let request_api = {
          sup_id: item.sup_id,
          outlet_id: this.selectedOutlet.id,
          barcode_date: item.barcode_date
        }
        itemsArray.push(request_api)
      }

      let requestBody = {
        items: itemsArray
      }

      this.$http
        .post(
          `${process.env.VUE_APP_API_URL}/dashboard/warehouse-outbounds/printed-barcode/request`,
          requestBody
        )
        .then(response => {
          if (response.status === 200) {
            this.barcodes = response.data.data.barcodes
            let file = new Blob([this.barcodes], {
              type: 'text/plain;charset=utf-8'
            })
            saveAs(file, 'barcodes.txt')
            this.print_dialog = false
            // window.location.reload()
          } else {
            this.$toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Gagal mencetak barcode',
              life: 3000
            })
            this.print_dialog = false
          }
        })
    },

    scrollToTop(refName) {
      this.$nextTick(() => {
        const component = this.$refs[refName]
        let element

        if (component && component.$el) {
          element = component.$el
        } else if (component) {
          element = component
        }

        if (element) {
          const topPosition =
            element.getBoundingClientRect().top + window.pageYOffset
          window.scrollTo({
            top: topPosition - 100,
            behavior: 'smooth'
          })
        }
      })
    },

    printBarcode() {
      this.is_error.outlet = false
      this.error_msg.outlet = ''

      if (!this.selectedOutlet || !this.selectedOutlet.id) {
        this.is_error.outlet = true
        this.error_msg.outlet = 'Outlet Tidak Boleh Kosong!'
        this.scrollToTop('outletDropdown')
        this.print_dialog = false
        return
      }
      this.list_request = []
      for (let i = 0; i < this.selectedItems.length; i++) {
        let item = this.selectedItems[i]
        let requestItem = {
          sup_id: item.sup_id,
          outlet_id: this.selectedOutlet.id,
          barcode_date: item.barcode_date
        }

        this.list_request.push(requestItem)
        this.print_dialog = true
      }
    },

    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped lang="scss">
.truncate-cell {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  width: 100%;
}

:deep(.confirmation-table) {
  .p-datatable-tbody > tr > td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 0;
  }

  .p-datatable-thead > tr > th {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.w-full {
  width: 100%;
}

.p-datatable-sm {
  .p-datatable-header {
    padding: 0.5rem;
  }

  .p-datatable-thead > tr > th {
    padding: 0.5rem;
  }

  .p-datatable-tbody > tr > td {
    padding: 0.5rem;
  }
}

.p-invalid {
  border-color: var(--red-500) !important;
}

.p-error {
  color: var(--red-500);
  font-size: 0.875rem;
  margin-top: 0.25rem;
}
</style>
